/**
 * PM Token billing constants
 *
 * Mirrors backend/shared/src/constants.rs PM Token Purchase block.
 * Keep in sync.
 */

/** Minimum PM token balance to access lobby, start games, or train */
export const MIN_PM_BALANCE = 1;

/**
 * Gross-margin overhead applied on top of raw LLM cost in retail PMT pricing.
 * Single source of truth for the margin knob — USD_PER_PM derives from this.
 * Mirror of backend/shared/src/constants.rs PMT_OVERHEAD_RATIO.
 */
export const PMT_OVERHEAD_RATIO = 0.3;

/** USD price per 1 PM token (web Stripe — $1 LLM base × (1 + PMT_OVERHEAD_RATIO)) */
export const USD_PER_PM = 1.0 + PMT_OVERHEAD_RATIO;

/** Minimum PM purchase PMT amount on web (callers multiply by USD_PER_PM for the Stripe charge) */
export const MIN_PM_PURCHASE = 5;

/** Preset PMT amounts shown as one-click buttons on /buy-tokens.
 *  Matches mobile IAP tier counts (5 / 10 / 20) so PMT amounts are
 *  consistent across platforms; only the USD charge varies by platform. */
export const PM_PURCHASE_PRESETS = [5, 10, 20] as const;

/**
 * IAP product IDs (iOS + Android). MUST stay in sync with
 * backend/shared/src/constants.rs IAP_PRODUCTS and the App Store Connect /
 * Play Console product catalogue.
 *
 * Immutable once published — adding a new tier means a new productId, never
 * a rename of an existing one.
 */
export const IAP_PRODUCT_IDS = [
  'com.polymyth.tokens.pack_5',
  'com.polymyth.tokens.pack_10',
  'com.polymyth.tokens.pack_25',
] as const;

export const IAP_BUNDLE_ID_IOS = 'quest.polymyth.polymythmobile';
export const IAP_PACKAGE_NAME_ANDROID = 'com.polymyth.polymyth_mobile';

/**
 * Tavian-voiced canned text injected as an in-chat bubble when a player
 * attempts to send a message with insufficient PM token balance. The bubble
 * also renders an inline "Buy Tokens" button (see LOW_BALANCE_GATE_BUTTON_LABEL).
 *
 * The gate fires client-side from `pmBalance` cache for every LLM-spending
 * chat send (game `sendPlayerText`/`sendTypedIntent`, lobby `sendOneOnOne`,
 * `sendInnerVoice`, `sendPartyChat`, `sendCharChatMessage`) and from the web
 * `ChatPanel.handleSendMessage`. Server-side mirror is the 30s-cached
 * pre-check in lobby `dispatch_message`.
 */
export const LOW_BALANCE_GATE_TEXT =
  "You're out of Polymyth Tokens — the adventure pauses until you top up.";

/** Inline CTA label on the low-balance gate bubble. */
export const LOW_BALANCE_GATE_BUTTON_LABEL = 'Buy Tokens';

// ---------------------------------------------------------------------------
// Reward grants (mirror backend/shared/src/constants.rs, keep in sync)
// ---------------------------------------------------------------------------

/**
 * PM tokens granted to BOTH the inviter and the invited friend when a referral
 * code is redeemed. Mirror of REFERRAL_INVITER_GRANT_PM / REFERRAL_SIGNEE_BONUS_PM
 * (both 100.0). Top rung of the earn ladder: invite (100) > Discord (50) >
 * signup (20). Every rendered amount reads this constant, never a literal.
 */
export const REFERRAL_GRANT_PM = 100;

/**
 * PM tokens granted once for joining the Discord (verified via OAuth + guild
 * membership). Mirror of DISCORD_JOIN_GRANT_PM.
 */
export const DISCORD_JOIN_GRANT_PM = 50;

/**
 * PM tokens granted once for posting a review of Polymyth on X (verified async
 * by Tavian's daily X pass). Mirror of X_REVIEW_GRANT_PM.
 */
export const X_REVIEW_GRANT_PM = 50;
